import React from 'react'
import moment from 'moment'
import {Bar} from 'react-chartjs-2'
import 'chart.js/auto'
import type {IpiVisStatusOverTime} from './_requests'

type Props = {
  data: IpiVisStatusOverTime[]
  isMonthlyBucketed?: boolean
}

const COLORS = {
  filled: '#5cb585',
  abandoned: '#f3bec0',
  cancelled: '#cccccc',
  open: '#ee5d61',
}

const BAR_SIZING = {maxBarThickness: 56, categoryPercentage: 0.6, barPercentage: 0.9}

const legendSpacing = {
  id: 'legendSpacing',
  beforeInit(chart: any) {
    const originalFit = chart.legend.fit
    chart.legend.fit = function () {
      originalFit.call(this)
      this.height += 28
    }
  },
}

const FONT_STACK = 'Lexend, sans-serif'
const BAR_TOTAL_FONT_PX = 13
const SEGMENT_FONT_PX = 11
const MIN_SEGMENT_HEIGHT_PX = SEGMENT_FONT_PX + 6
const SEGMENT_LABEL_PADDING_PX = 4

function stackTotal(chart: any, index: number): number {
  const datasets = chart.data.datasets || []
  let total = 0
  for (let di = 0; di < datasets.length; di++) {
    if (!chart.isDatasetVisible(di)) continue
    total += Number(datasets[di].data?.[index]) || 0
  }
  return total
}

const barTotals = {
  id: 'barTotals',
  afterDatasetsDraw(chart: any) {
    const {ctx, scales} = chart
    const datasets = chart.data.datasets || []
    const count = chart.data.labels?.length || 0
    ctx.save()
    ctx.font = `700 ${BAR_TOTAL_FONT_PX}px ${FONT_STACK}`
    ctx.fillStyle = '#171a1f'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    for (let i = 0; i < count; i++) {
      const total = stackTotal(chart, i)
      if (total <= 0) continue
      let x: number | null = null
      for (let di = 0; di < datasets.length; di++) {
        if (!chart.isDatasetVisible(di)) continue
        const el = chart.getDatasetMeta(di).data?.[i]
        if (el) {
          x = (el.getProps(['x'], true) as {x: number}).x
          break
        }
      }
      if (x === null) continue
      const y = scales.y.getPixelForValue(total)
      ctx.fillText(String(total), x, y - 6)
    }
    ctx.restore()
  },
}

const segmentPercents = {
  id: 'segmentPercents',
  afterDatasetsDraw(chart: any) {
    const {ctx} = chart
    const datasets = chart.data.datasets || []
    const count = chart.data.labels?.length || 0
    ctx.save()
    ctx.font = `600 ${SEGMENT_FONT_PX}px ${FONT_STACK}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    for (let i = 0; i < count; i++) {
      const total = stackTotal(chart, i)
      if (total <= 0) continue
      for (let di = 0; di < datasets.length; di++) {
        if (!chart.isDatasetVisible(di)) continue
        const value = Number(datasets[di].data?.[i]) || 0
        if (value <= 0) continue
        const el = chart.getDatasetMeta(di).data?.[i]
        if (!el) continue
        const {x, y, base, width} = el.getProps(['x', 'y', 'base', 'width'], true) as {
          x: number
          y: number
          base: number
          width: number
        }

        const height = Math.abs(base - y)
        if (height < MIN_SEGMENT_HEIGHT_PX) continue

        const pct = Math.round((value / total) * 100)
        if (pct <= 0) continue
        const text = `${pct}%`
        if (ctx.measureText(text).width > width - SEGMENT_LABEL_PADDING_PX) continue

        ctx.fillStyle = '#ffffff'
        ctx.fillText(text, x, (y + base) / 2)
      }
    }
    ctx.restore()
  },
}

export const AppointmentStatusChart: React.FC<Props> = ({data, isMonthlyBucketed}) => {
  const chartData = {
    labels: data.map((d) => {
      const parsed = moment(d.label, ['YYYY-MM-DD', moment.ISO_8601], true)
      if (!parsed.isValid()) return d.label
      return parsed.format(isMonthlyBucketed ? 'MMM YYYY' : 'DD/MM/YYYY')
    }),
    datasets: [
      {label: 'Filled', data: data.map((d) => d.filled), backgroundColor: COLORS.filled, ...BAR_SIZING},
      {label: 'Abandoned', data: data.map((d) => d.abandoned), backgroundColor: COLORS.abandoned, ...BAR_SIZING},
      {label: 'Canceled', data: data.map((d) => d.cancelled), backgroundColor: COLORS.cancelled, ...BAR_SIZING},
      {label: 'Open', data: data.map((d) => d.open), backgroundColor: COLORS.open, ...BAR_SIZING},
    ],
  }

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'center',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          padding: 20,
          font: {size: 13},
          generateLabels: (chart: any) =>
            chart.data.datasets.map((ds: any, i: number) => ({
              text: `   ${ds.label}`,
              fillStyle: ds.backgroundColor,
              strokeStyle: ds.backgroundColor,
              lineWidth: 0,
              hidden: !chart.isDatasetVisible(i),
              datasetIndex: i,
              index: i,
              pointStyle: 'circle',
            })),
        },
      },
      tooltip: {mode: 'index', intersect: false},
    },
    scales: {
      x: {
        stacked: true,
        grid: {display: false, drawBorder: false},
        ticks: {font: {size: 9}, color: '#9095a1', maxRotation: 0, autoSkip: true},
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grace: '8%',
        grid: {display: false, drawBorder: false},
        ticks: {display: false},
      },
    },
  }

  return (
    <div className='ipivis-card ipivis-chart-card'>
      {data.length === 0 ? (
        <div className='ipivis-empty'>No appointments in the selected range.</div>
      ) : (
        <div className='ipivis-chart-card__canvas'>
          <Bar data={chartData} options={options} plugins={[legendSpacing, barTotals, segmentPercents]} />
        </div>
      )}
    </div>
  )
}
